<script>
/**
 * ****************************************************************************
 * @description     :   Input Component
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

/**
 * Import template
 */
import helpers from '@/utils/helpers';
import template from './template.html';
import * as AutoKana from 'vanilla-autokana';

/**
 * Define content of input component
 */
var AnsInput = {
    template: template,
    props: {
        id: String,
        label: String,
        style: String,
        itemClass: [String, Object],
        buttonClass: String,
        buttonSecondClass: String,
        labelClass: String,
        labelBefore: String,
        labelAfter: String,
        inputClass: [String, Object],
        placeholder: String,
        value: String,
        modelValue: [String, Number],
        isRequired: Boolean,
        checkRequiredExternal: {
            type: Object,
            default: () => ({
                isCheck: false,
                isValid: true
            })
        },
        isDisabled: Boolean,
        isDisabledIcon: Boolean,
        isDisabledSecondIcon: Boolean,
        isReadonly: Boolean,
        isMobile: {
            type: Boolean,
            default: false
        },
        isAlwaysEnable: {
            type: Boolean,
            default: false
        },
        type: String,
        isDecimal: {
            type: Boolean,
            default: false
        },
        isEnableIME: {
            type: Boolean,
            default: false
        },
        isAddress: {
            type: Boolean,
            default: false
        },
        isJpFurigana: {
            type: Boolean,
            default: false
        },
        isHalfWidthKana: {
            type: Boolean,
            default: false
        },
        isAutoKana: {
            type: Array,
            default: []
        },
        decimal: Number,
        isNegative: Boolean,
        isNotShowCurrency: Boolean,
        maxLength: Number,
        cols: Number,
        rows: Number,
        icon: String,
        secondIcon: String,
        buttonLabel: {
            type: String,
            default: ''
        },
        secondButtonLabel: {
            type: String,
            default: ''
        },
        isInputGroup: Boolean,
        isSecondInputGroup: Boolean,
        onChange: Function,
        onFocus: Function,
        onKeyDown: Function,
        onKeyUp: Function,
        onBlur: Function,
        onClick: Function,
        onSecondClick: Function,
        isStartWithZero: {
            type: Boolean,
            default: true
        },
        isUppercase: {
            type: Boolean,
            default: false
        },
        isHorizontal: {
            type: Boolean,
            default: false
        },
        labelAfterClass: {
            type: String,
            default: ''
        },
        inputGroupClass: {
            type: String,
            default: ''
        },
        errors: {
            type: Array
        },
        checkValidateInputPassword: {
            type: Boolean,
            default: false
        },
        isAutocomplete: {
            type: Boolean,
            default: false
        },
        tabindex: {
            type: Number,
            default: 1
        },
        onlyUpdateValueWhenChange: {
            type: Boolean,
            default: false
        },
        shouldPad: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'referKana', 'validateSuccess'],
    data() {
        return {
            isFocus: false,
            realMaxLength: undefined,
            autoKana: undefined,
            modeDisabled: false,
            hasErrorMessage: false,
            inputChangeKana: false
        };
    },
    mounted() {
        this.realMaxLength = this.getRealMaxLength();
        if (this.isAutoKana && this.isAutoKana.length > 1) {
            this.$nextTick(() => {
                this.initializeAutoKana();
            });
        }
        if (
            ![
                'CUS_Login',
                'MNG_Login',
                'App_Login',
                'CUS_System901',
                'CUS_System902',
                'MNG_System901',
                'MNG_System902',
                'MNG_Order001',
                'MNG_System002',
                'MNG_System004',
                'MNG_Order004',
                'MNG_File001',
                'CUS_Order021',
                'CUS_Billing001',
                'CUS_Client003',
                'CUS_Delivery001',
                'CUS_System004'
            ].includes(this.$route.name)
        ) {
            this.modeDisabled = helpers.checkAuthorityMode(this.$route.name) && !this.isAlwaysEnable;
        }
    },
    computed: {
        vewValue: {
            get() {
                let val = this.convertNumber(this.modelValue ? this.modelValue : this.value);
                if (this.isFocus && !this.isDisabled && !this.isReadonly) {
                    if (this.shouldPad) {
                        val = this.clearPadZero(val);
                    }

                    if (this.type == 'money') {
                        val = val.replace(/¥/g, '');
                    }
                    // eslint-disable-next-line prettier/prettier
                    return this.type == 'money' || (this.type == 'measure' && !this.isDecimal)
                        ? val.replace(/,/g, '')
                        : this.type == 'postcode'
                        ? val.replace(/-/g, '')
                        : val;
                }
                return val;
            },
            set() {}
        },
        customizeItemClass() {
            const baseClass = {
                'd-flex': true,
                'item-component': true,
                'position-relative': true
            };

            const addClass = {
                ...(this.isMobile ? { 'in-mobile': true } : {}),
                ...(this.vewValue != null && this.vewValue != undefined && this.vewValue != '' ? { 'have-value': true } : {}),
                ...(this.isHorizontal ? { 'd-flex align-items-center': true } : {})
            };

            if (typeof this.itemClass === 'object' && this.itemClass !== null) {
                return { ...baseClass, ...addClass, ...this.itemClass };
            } else if (typeof this.itemClass === 'string' && this.itemClass) {
                return { ...baseClass, ...addClass, [this.itemClass]: true };
            }

            return { ...baseClass, ...addClass };
        },
        renderErrorMsg() {
            let message = '';
            this.hasErrorMessage = false;
            if (this.errors) {
                this.errors.forEach((item) => {
                    if (item.name == this.id) {
                        message = item.message;
                        this.hasErrorMessage = true;
                    }
                });
            }
            return message;
        },
        validateInputPassword() {
            if (this.checkValidateInputPassword) {
                if (this.modelValue && !this.validateHalfSizeAlphanumeric(this.modelValue)) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        },
        checkRequire() {
            if (this.checkRequiredExternal.isCheck) {
                return this.checkRequiredExternal.isValid;
            } else {
                return !!(this.vewValue.trim() && this.validateInputPassword && !this.renderErrorMsg);
            }
        },
        customizeInputClass() {
            const { hasErrorMessage, inputClass, isRequired, type, isMobile, vewValue, isEnableIME, isUppercase, isDecimal, checkRequire } = this;

            const baseClass = { 'form-control': true, 'custom-input': true, 'font-size-12-px': true, 'h-30': true };

            const addClass = {
                ...(hasErrorMessage ? { 'border-error': true } : {}),
                ...(isRequired ? { required: true } : {}),
                ...(type == 'number' || type == 'money' || type == 'measure' ? { [type]: true } : {}),
                ...(type == 'postcode' ? { number: true } : {}),
                ...(isMobile && vewValue ? { removebg: true } : {}),
                ...(isEnableIME ? { 'ime-active': true } : { 'disable-ime': true }),
                ...(isUppercase ? { 'text-uppercase': true } : {}),
                ...(checkRequire ? { 'bg-white': true } : {}),
                ...(type == 'number' || isDecimal ? { 'text-end': true } : {})
            };

            if (typeof inputClass === 'object' && inputClass !== null) {
                return { ...baseClass, ...addClass, ...inputClass };
            } else if (typeof inputClass === 'string' && inputClass) {
                return { ...baseClass, ...addClass, [inputClass]: true };
            }

            return { ...baseClass, ...addClass };
        },
        customizeTextareaClass() {
            const { inputClass, isRequired, type, isEnableIME, checkRequire } = this;

            const baseClass = { 'form-control': true, 'custom-textarea': true, 'font-size-12-px': true };

            const addClass = {
                ...(isRequired ? { required: true } : {}),
                ...(type == 'number' || type == 'money' || type == 'measure' ? { [type]: true } : {}),
                ...(isEnableIME ? { 'ime-active': true } : { 'disable-ime': true }),
                ...(checkRequire ? { 'bg-white': true } : {})
            };

            if (typeof inputClass === 'object' && inputClass !== null) {
                return { ...baseClass, ...addClass, ...inputClass };
            } else if (typeof inputClass === 'string' && inputClass) {
                return { ...baseClass, ...addClass, [inputClass]: true };
            }

            return { ...baseClass, ...addClass };
        }
    },
    methods: {
        /**
         * remove leading zero when focus
         * -----------------------------------------
         * @author : PhuongDT - 2026/03/18 - update
         * @params : value
         * @access : private
         */
        clearPadZero(value) {
            if (this.type !== 'text' && this.type !== 'number' && this.type !== 'upperAlphanumeric') {
                return value ?? '';
            }

            if (!this.shouldPad || !this.maxLength) {
                return value ?? '';
            }

            if (value === undefined || value === null || value === '') {
                return '';
            }

            const stringValue = String(value);

            // Chỉ xử lý cho chuỗi số
            if (!/^\d+$/.test(stringValue)) {
                return stringValue;
            }

            const clearedValue = stringValue.replace(/^0+/, '');
            return clearedValue === '' ? '0' : clearedValue;
        },

        /**
         * add leading zero when blur
         * -----------------------------------------
         * @author : PhuongDT - 2026/03/18 - update
         * @params : value
         * @access : private
         */
        addPadZero(value) {
            if (this.type !== 'text' && this.type !== 'number' && this.type !== 'upperAlphanumeric') {
                return value ?? '';
            }

            if (!this.shouldPad || !this.maxLength) {
                return value ?? '';
            }

            if (value === undefined || value === null || value === '') {
                return '';
            }

            const stringValue = String(value);

            // Chỉ xử lý cho chuỗi số
            if (!/^\d+$/.test(stringValue)) {
                return stringValue;
            }

            return stringValue.padStart(this.maxLength, '0');
        },
        /**
         * initialize autokana
         * -----------------------------------------
         * @author : ManhND - 2024/07/09 - create
         * @params :
         * @access : private
         */
        initializeAutoKana() {
            const [kanaInput, kanaOuput] = this.isAutoKana;
            this.autoKana = AutoKana.bind(kanaInput, kanaOuput, {
                katakana: true
            });
            this.autoKana.stop();
        },
        /**
         * convert number type for input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        convertNumber(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            value = value + '';
            let values = [];
            let negative = '';
            let dot = '';
            let afterDot = '';
            if (this.type == 'number' || this.type == 'money' || this.type == 'measure') {
                if (this.isDecimal) {
                    this.realMaxLength = this.maxLength + 1;
                }

                if (this.isNegative) {
                    negative = '-';
                }
                values = value.split('.');
                values[0] = values[0].replace(/\D/g, '');

                if (values.length > 1) {
                    afterDot = values[1].replace(/\D/g, '');
                    if (afterDot.length > this.decimal) {
                        afterDot = afterDot.substring(0, this.decimal);
                    }
                    dot = '.';
                    this.isDecimal ? (value = negative + values[0] + dot + afterDot) : (value = negative + values[0]);
                }

                if (this.type == 'measure' && !this.isDecimal) {
                    values[0] = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    value = negative + values[0];
                }

                if (this.type == 'money') {
                    values[0] = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    if (values[0].length > 1) {
                        values[0] = values[0].replace(/^0+/, '');
                    }
                    if (values[0]) {
                        value = negative + (this.isNotShowCurrency ? '' : '¥') + values[0] + (values.length > 1 ? '.' + values[1] : '');
                    } else {
                        value = negative + (this.isNotShowCurrency ? '' : '¥') + values[0] + (values.length > 1 ? '.' + values[1] : '');
                    }
                }
                // author: trinhdtk - ANS845
                // if type is number, and not allow start with zero
                // Eg: 0123 => 123
                if (this.type == 'number' && !this.isStartWithZero) {
                    values[0] = values[0].replace(/^0+/, '');
                    value = values[0];
                }

                if (this.isDecimal && this.decimal > 0 && afterDot === '' && value.length > this.maxLength - this.decimal) {
                    dot = '.';
                    afterDot = values[0].substring(this.maxLength - this.decimal, value.length);
                    values[0] = values[0].substring(0, this.maxLength - this.decimal);
                    this.isDecimal ? (value = negative + values[0] + dot + afterDot) : (value = negative + values[0]);
                }

                if (!values[0]) {
                    values[0] = 0;
                }
            }

            if (this.type == 'postcode') {
                const postcode = value.replace(/\D/g, '');
                if (postcode.length >= 7) {
                    value = postcode.slice(0, 3) + '-' + postcode.slice(3, 7);
                } else {
                    value = '';
                }
            }
            return value;
        },
        /**
         * get max lenght input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        getRealMaxLength(e) {
            if (this.maxLength == undefined || this.maxLength == null || this.type != 'money' || this.type != 'measure') {
                let maxLength = this.maxLength;
                if (e) {
                    if (this.type == 'number') {
                        if (e.target.value.indexOf('-') >= 0) {
                            maxLength += 1;
                        }
                        if (e.target.value.indexOf('.') >= 0) {
                            maxLength += 1;
                        }
                    }
                    if (this.type == 'postcode') {
                        if (e.target.value.indexOf('-') >= 0) {
                            maxLength += 1;
                        }
                    }
                }
                return maxLength;
            }
            return this.maxLength + (parseInt(this.maxLength / 3) + (this.maxLength % 3 > 0 ? 0 : -1));
        },
        /**
         * event forcus on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        focusInput(e) {
            //e.target.select();
            this.$refs.input.focus();
            this.isFocus = true;
            this.realMaxLength = this.maxLength;

            if (this.shouldPad && this.$refs.input) {
                const clearedValue = this.clearPadZero(this.$refs.input.value);
                this.$refs.input.value = clearedValue;
                this.$emit('update:modelValue', clearedValue);
            }

            if (this.onFocus) {
                this.onFocus(e);
            }
        },
        /**
         * event blur on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        blurInput(e) {
            this.isFocus = false;
            this.realMaxLength = this.getRealMaxLength(e);
            if (this.type == 'tel') {
                this.validateTel(e.target.value, e.target);
            }
            if (this.autoKana && this.inputChangeKana) {
                this.validateInputAutoKana(this.autoKana.elName);
                this.$emit('referKana', this.autoKana);
            }
            if (this.checkValidateInputPassword) {
                let status = true;
                if (this.modelValue && !this.validateHalfSizeAlphanumeric(this.modelValue)) {
                    status = false;
                }
                this.$emit('validateSuccess', status);
            }

            if (this.shouldPad && e?.target) {
                const paddedValue = this.addPadZero(e.target.value);
                e.target.value = paddedValue;
                this.$emit('update:modelValue', paddedValue);
            }

            if (this.onBlur) {
                this.onBlur(e);
            }
        },
        /**
         * validate only accept japanese's character, number, and special character when blur
         * -----------------------------------------
         * @author : ManhND - 2024/07/12 - create
         * @params :
         * @access : private
         */
        validateInputAutoKana(itemAutoKana) {
            const string = itemAutoKana.value;
            const japaneseRegex = /^[\u3040-\u30FF\u4E00-\u9FAF0-9\s\p{P}\p{S}]*$/u;
            if (!japaneseRegex.test(string)) {
                // itemAutoKana.value = '';
                // itemFurigana.value = '';
                this.updateInput();
            }
        },
        /**
         * validate tel when blur
         * -----------------------------------------
         * @author : ManhND - 2024/07/12 - create
         * @params :
         * @access : private
         */
        validateTel(value, input) {
            let tel = '';
            const regexOnlyNumber = /^\d+$/;
            const regexTel11 = /^\d{3}-\d{4}-\d{4}$/;
            const regexTel10 = /^\d{2}-\d{4}-\d{4}$/;
            const string = value.replace(/\D/g, '');
            if (regexOnlyNumber.test(string)) {
                if (string.length == 11) {
                    tel = `${string.slice(0, 3)}-${string.slice(3, 7)}-${string.slice(7, 11)}`;
                } else if (string.length == 10) {
                    tel = `${string.slice(0, 2)}-${string.slice(2, 6)}-${string.slice(6, 10)}`;
                } else {
                    tel = string;
                }
            } else {
                if (regexTel11.test(string) || regexTel10.test(string)) {
                    tel = string;
                }
            }

            input.value = tel;
            this.$emit('update:modelValue', tel);
        },
        /**
         * event keydown on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputKeydown(e) {
            const keyCode = e.keyCode || e.which;
            const keyCodeSpecial =
                keyCode === 35 || // end
                keyCode === 36 || // home
                keyCode === 116 || // f5
                keyCode === 37 || // arrow left
                keyCode === 38 || // arrow up
                keyCode === 39 || // arrow right
                keyCode === 40 || // arrow down
                // keyCode === 8 || // backspace
                keyCode === 9; //tab
            if (!keyCodeSpecial) {
                if (this.autoKana != undefined) {
                    this.inputChangeKana = true;
                    this.autoKana.start();
                }
            }
            if (this.type == 'number' && this.isDecimal) {
                return this.inputOnlyDecimal(e);
            }
            if (this.type == 'number' || this.type == 'money' || this.type == 'measure' || this.type == 'postcode') {
                return this.inputOnlyNumber(e);
            }
            if (this.type == 'tel') {
                return this.inputOnlyTel(e);
            }
            // if (this.isAutoKana.length > 0) {
            //     const keyCode = e.keyCode || e.which;
            //     if (keyCode === 9) {
            //         e.preventDefault();
            //         return false;
            //     }
            // }
            if (this.type === 'upperAlphanumeric') {
                const keyCodeCommon =
                    keyCode === 35 || // end
                    keyCode === 36 || // home
                    keyCode === 116 || // f5
                    keyCode === 37 || // arrow left
                    keyCode === 39 || // arrow right
                    keyCode === 8 || // backspace
                    keyCode === 9; //tab
                const alphaNumeric =
                    (keyCode >= 65 && keyCode <= 90) || // a-z
                    (keyCode >= 48 && keyCode <= 57) || // 0-9
                    (keyCode >= 96 && keyCode <= 105); // numpad 0-9)

                // prevent
                if (
                    (e.shiftKey && keyCode >= 48 && keyCode <= 57) || // 0-9
                    !(alphaNumeric || keyCodeCommon)
                ) {
                    e.preventDefault();
                    return false;
                }
            }
        },
        inputKeyup(e) {
            let maxLength = this.maxLength;
            if (this.type == 'number') {
                if (e.target.value.indexOf('-') >= 0) {
                    maxLength += 1;
                }
                if (e.target.value.indexOf('.') >= 0) {
                    maxLength += 1;
                }
            }
            if (this.type == 'postcode') {
                if (e.target.value.indexOf('-') >= 0) {
                    maxLength += 1;
                }
            }
            if (this.type == 'measure' && this.isDecimal) {
                if (e.target.value.indexOf('.') >= 0) {
                    maxLength += 1;
                }
            }
            this.realMaxLength = maxLength;
            if (this.type == 'postcode' && this.realMaxLength != e.target.value.length) {
                return false;
            }

            if (!this.onlyUpdateValueWhenChange) {
                if (this.isUppercase) {
                    this.$emit('update:modelValue', e.target.value.toUpperCase());
                } else {
                    this.$emit('update:modelValue', e.target.value);
                }
            }
            return true;
        },
        keyUpTextarea(e) {
            const val = e.target.value;
            const value = this.isUppercase ? val.toUpperCase() : val;
            this.$emit('update:modelValue', value);

            if (this.onKeyUp) {
                this.onKeyUp(e);
            }
        },
        keyDownTextarea(e) {
            if (this.onKeyDown) {
                this.onKeyDown(e);
            }
        },
        /**
         * event block key on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputOnlyNumber(e) {
            if (
                !(
                    (e.keyCode > 47 && e.keyCode < 58) ||
                    (e.keyCode > 95 && e.keyCode < 106) ||
                    e.keyCode == 116 ||
                    e.keyCode == 46 ||
                    e.keyCode == 37 ||
                    e.keyCode == 39 ||
                    e.keyCode == 35 ||
                    e.keyCode == 36 ||
                    e.keyCode == 8 ||
                    e.keyCode == 9 ||
                    e.ctrlKey ||
                    e.keyCode == 229 ||
                    e.keyCode == 13 ||
                    e.keyCode == 189 ||
                    e.keyCode == 109 ||
                    (this.isDecimal && (e.keyCode == 190 || e.keyCode == 110))
                ) ||
                (!this.isNegative && (e.keyCode == 189 || e.keyCode == 109))
            ) {
                event.preventDefault();
                return false;
            }
            return true;
        },
        /**
         * event block key on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputOnlyDecimal(e) {
            if (
                !(
                    (e.keyCode > 47 && e.keyCode < 58) ||
                    (e.keyCode > 95 && e.keyCode < 106) ||
                    e.keyCode == 116 ||
                    e.keyCode == 46 ||
                    e.keyCode == 37 ||
                    e.keyCode == 39 ||
                    e.keyCode == 35 ||
                    e.keyCode == 36 ||
                    e.keyCode == 8 ||
                    e.keyCode == 9 ||
                    e.ctrlKey ||
                    e.keyCode == 229 ||
                    e.keyCode == 110 ||
                    e.keyCode == 190 ||
                    e.keyCode == 13
                ) ||
                (!this.isNegative && e.keyCode == 189) ||
                e.keyCode == 109
            ) {
                event.preventDefault();
                return false;
            }
            return true;
        },
        /**
         * event block key on input with type tel
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputOnlyTel(e) {
            if (
                !(
                    (!e.shiftKey && e.keyCode > 47 && e.keyCode < 58) ||
                    (!e.shiftKey && e.keyCode > 95 && e.keyCode < 106) ||
                    e.keyCode == 116 ||
                    e.keyCode == 46 ||
                    e.keyCode == 37 ||
                    e.keyCode == 39 ||
                    e.keyCode == 35 ||
                    e.keyCode == 36 ||
                    e.keyCode == 8 ||
                    e.keyCode == 9 ||
                    e.ctrlKey ||
                    e.keyCode == 229 ||
                    // e.keyCode == 107 ||
                    e.keyCode == 109 ||
                    (!e.shiftKey && e.keyCode == 189) ||
                    (e.shiftKey && e.keyCode == 57) ||
                    (e.shiftKey && e.keyCode == 48) ||
                    (e.shiftKey && e.keyCode == 187)
                )
            ) {
                event.preventDefault();
                return false;
            }
            return true;
        },
        /**
         * update input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        updateInput(e) {
            if (this.autoKana != undefined) {
                if (this.inputChangeKana) {
                    this.$emit('referKana', this.autoKana);
                    this.inputChangeKana = false;
                }
                this.autoKana.stop();
            }
            const val = this.convertNumber(this?.$refs?.input?.value);
            let value = this.type == 'money' || (this.type == 'measure' && !this.isDecimal) ? val.replace(/,/g, '') : val;
            if (this.type == 'money' || this.type == 'number') {
                let regex = '';
                if (this.isNegative) {
                    regex = this.isDecimal ? /([^0-9/.-])/g : /[^0-9/-]/g;
                } else {
                    regex = this.isDecimal ? /([^0-9/.])/g : /[^0-9]/g;
                }
                value = value.replace(regex, '');
                if (!value) e.target.value = '';
            }
            if (!this.isAddress) {
                if (!this.isEnableIME) {
                    const regex = /[一-龠ぁ-ゔァ-ヴーａ-ｚＡ-Ｚ０-９々〆〤]+/u;
                    value = value.replace(regex, '');
                } else {
                    const regex = /[ｧ-ﾝﾞﾟ]+/u;
                    value = value.replace(regex, '');
                }
            }
            if (this.type == 'measure' && this.isDecimal) {
                const values = val.split('.');
                let dot = '';
                let afterDot = '';
                if (values.length > 1) {
                    afterDot = values[1].replace(/\D/g, '');
                    if (afterDot.length > this.decimal) {
                        afterDot = afterDot.substring(0, this.decimal);
                    }
                    dot = '.';
                }
                value = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + dot + afterDot;
            }
            if (this.type === 'upperAlphanumeric') {
                value = value.replace(/[^A-Za-z0-9]/g, '');
            }
            if (this.isHalfWidthKana) {
                const newValue = val.replace(/[\u200B-\u200D\uFEFF]/g, '');
                // \u0020: half-width space
                const regex = /^([\uFF65-\uFF9F]|\u0020)+$/;
                if (regex.test(newValue)) {
                    value = newValue;
                } else {
                    value = '';
                }
            } else if (this.isJpFurigana) {
                const newValue = val.replace(/[\u200B-\u200D\uFEFF]/g, '');
                // \u0020: half-width space
                // \u3000: full-width space
                const regex = /^([ァ-ヴー]|ー|\u0020|\u3000)+$/;
                if (regex.test(newValue)) {
                    value = newValue;
                } else {
                    value = '';
                }
            }
            this.$emit('update:modelValue', value);
            if (this.onChange) {
                this.onChange(e, value);
                helpers.handleAddRemoveClass([
                    {
                        ele: e.target,
                        arrAddRemove: ['require-input'],
                        type: 0
                    }
                ]);
            }
        },
        validateHalfSizeAlphanumeric(string) {
            var regexp = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[#?!@$%^&*-])[A-Za-z\d#?!@$%^&*-]{10,}$/;
            if (regexp.test(string) || string == '') {
                return true;
            } else {
                return false;
            }
        }
    }
};
export default AnsInput;
</script>
